import type { CreateContractInput, UpdateContractInput } from '@classgo/shared';

import type { ContractView } from '../api/types';
import type { ContractFormValues } from './types';

const number = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const integer = (value: string): number | undefined => {
  const parsed = number(value);
  return parsed === undefined ? undefined : Math.trunc(parsed);
};

const text = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value.trim();

/**
 * A blank form: starts today, monthly, list prices left for the person to type.
 *
 * @param {string} startsOn -- The default start date.
 * @returns The values.
 */
export const emptyContractForm = (startsOn: string): ContractFormValues => ({
  startsOn,
  endsOn: '',
  billingCycle: 'monthly',
  platformFee: '',
  pricePerStudent: '',
  discountPercent: '0',
  courtesyStartsOn: '',
  courtesyEndsOn: '',
  mentoriaEnabled: false,
  mentoriaPricePerStudent: '',
  mentoriaMonthlyQuota: '',
  mentoriaModelId: '',
  mentoriaFamilyPrice: '',
  notes: '',
  status: 'active',
});

/**
 * The form for an existing contract.
 *
 * @param {ContractView} contract -- The contract.
 * @returns The values.
 */
export const contractToForm = (contract: ContractView): ContractFormValues => ({
  startsOn: contract.startsOn,
  endsOn: contract.endsOn ?? '',
  billingCycle: contract.billingCycle === 'annual' ? 'annual' : 'monthly',
  platformFee: String(contract.platformFee),
  pricePerStudent: String(contract.pricePerStudent),
  discountPercent: String(contract.discountPercent),
  courtesyStartsOn: contract.courtesyStartsOn ?? '',
  courtesyEndsOn: contract.courtesyEndsOn ?? '',
  mentoriaEnabled: contract.mentoriaEnabled,
  mentoriaPricePerStudent:
    contract.mentoriaPricePerStudent === null ? '' : String(contract.mentoriaPricePerStudent),
  mentoriaMonthlyQuota:
    contract.mentoriaMonthlyQuota === null ? '' : String(contract.mentoriaMonthlyQuota),
  mentoriaModelId: contract.mentoriaModelId ?? '',
  mentoriaFamilyPrice:
    contract.mentoriaFamilyPrice === null ? '' : String(contract.mentoriaFamilyPrice),
  notes: contract.notes ?? '',
  status: contract.status === 'ended' ? 'ended' : 'active',
});

/**
 * What `POST /staff/tenants/{id}/contracts` receives: empty strings become absent fields,
 * numbers are parsed the Colombian way (`1.500.000` and `1500000` both work).
 *
 * @param {ContractFormValues} form -- The values.
 * @returns The input.
 */
export const contractFormToCreate = (form: ContractFormValues): CreateContractInput => ({
  startsOn: form.startsOn,
  ...(text(form.endsOn) ? { endsOn: form.endsOn } : {}),
  billingCycle: form.billingCycle,
  platformFee: number(form.platformFee) ?? 0,
  pricePerStudent: number(form.pricePerStudent) ?? 0,
  discountPercent: number(form.discountPercent) ?? 0,
  ...(text(form.courtesyStartsOn) ? { courtesyStartsOn: form.courtesyStartsOn } : {}),
  ...(text(form.courtesyEndsOn) ? { courtesyEndsOn: form.courtesyEndsOn } : {}),
  mentoriaEnabled: form.mentoriaEnabled,
  ...(form.mentoriaEnabled && number(form.mentoriaPricePerStudent) !== undefined
    ? { mentoriaPricePerStudent: number(form.mentoriaPricePerStudent) }
    : {}),
  ...(form.mentoriaEnabled && integer(form.mentoriaMonthlyQuota) !== undefined
    ? { mentoriaMonthlyQuota: integer(form.mentoriaMonthlyQuota) }
    : {}),
  ...(form.mentoriaEnabled && text(form.mentoriaModelId)
    ? { mentoriaModelId: form.mentoriaModelId.trim() }
    : {}),
  ...(form.mentoriaEnabled && number(form.mentoriaFamilyPrice) !== undefined
    ? { mentoriaFamilyPrice: number(form.mentoriaFamilyPrice) }
    : {}),
  ...(text(form.notes) ? { notes: form.notes.trim() } : {}),
});

/**
 * What `PATCH …/contracts/{contractId}` receives: every field, cleared ones as `null`.
 *
 * @param {ContractFormValues} form -- The values.
 * @returns The input.
 */
export const contractFormToUpdate = (form: ContractFormValues): UpdateContractInput => ({
  status: form.status,
  endsOn: text(form.endsOn) ?? null,
  billingCycle: form.billingCycle,
  platformFee: number(form.platformFee) ?? 0,
  pricePerStudent: number(form.pricePerStudent) ?? 0,
  discountPercent: number(form.discountPercent) ?? 0,
  courtesyStartsOn: text(form.courtesyStartsOn) ?? null,
  courtesyEndsOn: text(form.courtesyEndsOn) ?? null,
  mentoriaEnabled: form.mentoriaEnabled,
  mentoriaPricePerStudent: form.mentoriaEnabled
    ? (number(form.mentoriaPricePerStudent) ?? null)
    : null,
  mentoriaMonthlyQuota: form.mentoriaEnabled ? (integer(form.mentoriaMonthlyQuota) ?? null) : null,
  mentoriaModelId: form.mentoriaEnabled ? (text(form.mentoriaModelId) ?? null) : null,
  mentoriaFamilyPrice: form.mentoriaEnabled ? (number(form.mentoriaFamilyPrice) ?? null) : null,
  notes: text(form.notes) ?? null,
});
